'use client';

import { useEffect, useState } from 'react';

export interface Fraktoffert {
  shipping: number;
  surcharge: number;
  surcharge_label: string | null;
  surcharge_class: string | null;
  /** En rad per skrymmande vara som debiteras — för att kunna visa varför. */
  surcharge_lines?: Array<{ id?: number; label: string; klass: string; antal: number; belopp: number }>;
}

/**
 * Vad frakten FAKTISKT kostar för de här varorna — från samma svar som kassan
 * tar betalt efter.
 *
 * Varukorgen och minikorgen räknade tidigare bara på ordervärdet: över 5 000 kr
 * stod det "Fri frakt — gratis hemleverans". Men fri frakt gäller inte långgods
 * och pall. En Adblue-tank för 17 290 kr visade alltså "Fri frakt" i korgen och
 * 450 kr i kassan (ägaren 2026-09-16). Ett brutet löfte är värre än ett dyrt
 * pris: kunden hinner bestämma sig innan hon ser vad det kostar.
 *
 * Faller anropet bort returneras null, och den som frågar visar sin gamla text.
 * Hellre tyst än fel — men aldrig ett löfte vi inte kan hålla.
 */
export interface Fraktvara { id: number; antal: number }

export function useFraktoffert(
  varor: Fraktvara[],
  subtotal: number,
  aktiv = true,
  hamtaIButik = false,
): Fraktoffert | null {
  const [offert, setOffert] = useState<Fraktoffert | null>(null);
  // "12x2,13" — antalet måste med, annars räknar servern en av varje och kunden
  // ser ett lägre påslag än hon debiteras.
  const nyckel = varor.map((v) => (v.antal > 1 ? `${v.id}x${v.antal}` : String(v.id))).join(',');

  useEffect(() => {
    if (!aktiv || !nyckel || subtotal <= 0) { setOffert(null); return; }
    let avbruten = false;
    // Liten fördröjning så att varje klick i en antalsruta inte blir ett anrop.
    const t = setTimeout(() => {
      fetch(`${process.env.NEXT_PUBLIC_API_URL}/checkout/shipping-quote?ids=${nyckel}&subtotal=${subtotal}${hamtaIButik ? '&hamta=1' : ''}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (!avbruten && d) setOffert(d as Fraktoffert); })
        .catch(() => { /* trappan gäller tills vidare */ });
    }, 150);
    return () => { avbruten = true; clearTimeout(t); };
  }, [nyckel, subtotal, aktiv, hamtaIButik]);

  return offert;
}
